import { Component, OnInit, OnDestroy } from '@angular/core';
import { PageType, ComponentName, SelectionChangeType, EventMappingType, SpecialOfferType } from 'src/app/models/enums';
import { Subscription } from 'rxjs';
import { SportsService } from 'src/app/services/sports.service';
import { ISportRegionCompetitionSelection } from 'src/app/models/sportHierarchy/sportRegionCompetitionSelection.interface';
import { CacheService } from 'src/app/services/cache.service';
import { StorageService } from 'src/app/services/util/storage.service';
import { LoaderService } from 'src/app/services/loader.service';
import { UtilityService } from 'src/app/services/util/utility.service';
import { IEventsGroupComponentViewModel } from 'src/app/models/event/eventsGroupComponentViewModel.interface';
import { CacheChangesService } from 'src/app/services/cacheChanges.service';
import { QueryParamsService } from 'src/app/services/http/queryParams.service';
import { ISrcSelectionIds } from 'src/app/models/util/srcSelectionIds.interface';
import { LocaleHelper } from 'src/locale/LocaleHelper';
import { EventsService } from 'src/app/services/events.service';
import { EventVM } from 'src/app/models/event/event';
import { ConstantValues } from 'src/app/models/util/constantValues';
import { ViewConfigurations } from 'src/app/models/util/viewConfigurations';

@Component({
  selector: 'app-sport-tree-selection-container',
  templateUrl: './sport-tree-selection-container.component.html',
  styleUrls: ['./sport-tree-selection-container.component.less']
})
export class SportTreeSelectionContainerComponent implements OnInit, OnDestroy {

  public pageType: PageType = ViewConfigurations.useSoonViewWhenTimeFilterIsChanged ? PageType.Soon : PageType.Prematch;
  public isLive = false;
  public pageTypeEnum = PageType;
  public isMobileView = false;
  public eventsGroup: IEventsGroupComponentViewModel = null;

  private language: string;
  private languageCulture: string;
  private dateToStringFormat: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' };

  protected sportRegionCompetitionSelection: ISportRegionCompetitionSelection;
  private subscription: Subscription = new Subscription();
  public updateGroupChanged = false;
  private sportStartingSoonOrMostPopularSportId: number;
  public currentPage = 1;
  public numberOfPages = 0;

  private eventTypesForStandadOffer = !ViewConfigurations.showSpecialEventsInStandardOffer ? [EventMappingType.EventOfDay, EventMappingType.StandardEvent] : [];

  constructor(
    private cache: CacheService,
    private cacheChanges: CacheChangesService,
    private sportsService: SportsService,
    private storageService: StorageService,
    private loaderService: LoaderService,
    private utilityService: UtilityService,
    private eventsService: EventsService,
    private queryParamService: QueryParamsService) {

    // added because on mobile view getEvents is called before onInit is called
    this.language = this.storageService.language;
    this.languageCulture = LocaleHelper.cultureName[this.language];
    this.pageType = this.queryParamService.pageType;

    this.isMobileView = utilityService.mobileView;
    if (this.isMobileView) {
      this.sportRegionCompetitionSelection = this.sportsService.sportRegionCompetitionSelection;
      this.getEvents();
    }
    this.subscription.add(sportsService.srcSelectionChanged$.subscribe((x) => {
      if (this.sportRegionCompetitionSelection !== this.sportsService.sportRegionCompetitionSelection) {
        this.sportRegionCompetitionSelection = this.sportsService.sportRegionCompetitionSelection;
      }
      if (x.callFrom === ComponentName.SportTreeSelectionContainerComponent || x.callFrom === ComponentName.SportsTreeBreadcrumbsComponent) {
        return;
      }
      if (x.selectionChangeType === SelectionChangeType.Sport && (!this.sportsService.sportRegionCompetitionSelection.sport || !this.sportsService.sportRegionCompetitionSelection.sport.specialOfferType)) {
        this.eventsGroup = null;
        return;
      }
      this.getEvents();
    }));
    this.subscription.add(this.queryParamService.pageTypeChanged$.subscribe((pt) => {
      this.eventsGroup = null;
      this.pageType = pt;
    }));
    this.subscription.add(this.cache.sportTreeElementsWithAddedRemovedEventsChanged$.subscribe(x => this.updatEventGroup(x)));
    this.subscription.add(this.sportsService.selectedMostPopularOrEventsSoonSportIdChanged$.subscribe(sIdAndCount => {
      if (this.sportStartingSoonOrMostPopularSportId !== sIdAndCount.sportId) {
        this.currentPage = 1;
      }

      this.numberOfPages = Math.floor(sIdAndCount.eventsCount / ConstantValues.NUMBER_OF_INITIAL_EVENTS_PM);
      this.numberOfPages = sIdAndCount.eventsCount % ConstantValues.NUMBER_OF_INITIAL_EVENTS_PM > 0 ? this.numberOfPages + 1 : this.numberOfPages;
      if (this.currentPage > this.numberOfPages) {
        this.currentPage = this.numberOfPages;
      } else if (this.currentPage < 1) {
        this.currentPage = 1;
      }

      this.sportStartingSoonOrMostPopularSportId = sIdAndCount.sportId;
      this.getEvents(true);
    }));

    if (ViewConfigurations.useSoonViewWhenTimeFilterIsChanged) {
      this.subscription.add(this.sportsService.timeFilterChanged$.subscribe(() => this.eventsGroup = null));
      this.subscription.add(this.cache.mostPopularOrSoonEventAddedRemoved$.subscribe(sportIds => {
        if (this.pageType === PageType.Soon || this.pageType === PageType.MostPopular) {
          if (sportIds.includes(this.sportStartingSoonOrMostPopularSportId)) {
            this.getEvents(true);
          }
        }
      }));
    } else {
      // was part of tasks #11382 #11374
      // this.subscription.add(this.sportsService.timeFilterChanged$.subscribe(() => {
      //   this.getEvents();
      // }));
    }
  }

  ngOnInit(): void {
    if (this.utilityService.mobileView) {
      this.dateToStringFormat = { weekday: 'short', month: '2-digit', day: '2-digit' };
    }
    this.language = this.storageService.language;
    this.languageCulture = LocaleHelper.cultureName[this.language];

    if (this.pageType === PageType.Live) {
      this.isLive = true;
    } else {
      this.isLive = false;
    }
    // console.log('sportTreeSelection language: ' + this.languageCulture);
    // console.log('sportTreeSelection languageCulture: ' + this.languageCulture);
    // console.log('sportTreeSelection dateToStringFormat: ' + this.dateToStringFormat.toString());
    // console.log('sportTreeSelection isMobile: ' + this.isMobileView);
  }

  changePage(changePageTo: number) {
    this.currentPage = changePageTo;
    this.getEvents(true);
    window.parent.scroll(0, 0);
  }

  private updatEventGroup(changedSportTreeElements: ISrcSelectionIds[]): void {
    if (this.pageType === PageType.Soon || this.pageType === PageType.MostPopular) {
      return;
    }
    // for prematch maybe for live will be grouped by sport
    for (const changedElement of changedSportTreeElements) {
      if (!this.eventsGroup) {
        continue;
      }
      if (!this.eventsGroup.specialOfferType) {
        if (this.eventsGroup.sportId === changedElement.sId
          && this.eventsGroup.regionId === changedElement.rId
          && (this.eventsGroup.competitionId === changedElement.cId || !this.eventsGroup.competitionId)) {
          this.cache.getEventsGroupForFilter(this.sportsService.timeFilterValueGet,
            this.languageCulture,
            this.dateToStringFormat,
            changedElement.sId,
            changedElement.rId,
            this.sportRegionCompetitionSelection.competition ? changedElement.cId : null,
            this.eventsGroup.specialOfferType).subscribe(group => {
              if (group && this.eventsGroup) {
                this.eventsGroup.events = group.events;
                this.cacheChanges.removeAllEventsChangesSubscription(this.isLive);
                this.cacheChanges.addAllEventsChangesSubscription(this.eventsGroup.events.map(e => e.id), this.isLive);
                // this.eventsGroup.events.map(e => this.cacheChanges.subscribeForAllEventChanges(e.id, this.isLive));
              }
            });
        }
      } else {
        if (this.eventsGroup && this.eventsGroup.sportId === changedElement.sId) {
          this.cache.getEventsGroupForSpecialOfferFilter(this.sportsService.timeFilterValueGet,
            this.languageCulture,
            this.dateToStringFormat,
            this.eventsGroup.sportId,
            this.eventsGroup.specialOfferType).subscribe(group => {
              if (group && this.eventsGroup) {
                this.eventsGroup.events = group.events;
                this.cacheChanges.removeAllEventsChangesSubscription(this.isLive);
                this.cacheChanges.addAllEventsChangesSubscription(this.eventsGroup.events.map(e => e.id), this.isLive);
                // this.eventsGroup.events.map(e => this.cacheChanges.subscribeForAllEventChanges(e.id, this.isLive));
              }
            });
        }
      }
    }
  }

  // Later add method to cache to get Events for special offers
  private getEvents(clearDataForPopularOrSoon?: boolean) {
    this.updateGroupChanged = !this.updateGroupChanged;
    this.cacheChanges.removeAllEventsChangesSubscription(this.isLive);
    this.eventsGroup = null;
    this.sportRegionCompetitionSelection = this.sportsService.sportRegionCompetitionSelection;
    if (this.pageType === PageType.Soon || this.pageType === PageType.MostPopular) {
      if (!this.sportStartingSoonOrMostPopularSportId) {
        return;
      }
      this.loaderService.showHideLoader(true);

      if (this.pageType === PageType.Soon) {
        this.getEventsStartingSoon(clearDataForPopularOrSoon);
      } else {
        this.getMostPopularEvents(clearDataForPopularOrSoon);
      }

    } else {
      if (!this.sportRegionCompetitionSelection.sport) {
        this.eventsGroup = null;
        this.loaderService.showHideLoader(false);
        return;
      }
      if (!this.sportRegionCompetitionSelection.sport.specialOfferType) {
        this.loaderService.showHideLoader(true);
        // protection added to clear all

        this.cache.getEventsGroupForFilter(
          this.sportsService.timeFilterValueGet,
          this.languageCulture,
          this.dateToStringFormat,
          this.sportRegionCompetitionSelection.sport ? this.sportRegionCompetitionSelection.sport.id : null,
          this.sportRegionCompetitionSelection.region ? this.sportRegionCompetitionSelection.region.id : null,
          this.sportRegionCompetitionSelection.competition ? this.sportRegionCompetitionSelection.competition.id : null,
          this.sportRegionCompetitionSelection.sport.specialOfferType).subscribe(
            x => {
              // if cache changes filter sport or competition is empty list it whill return all changes
              // this.cacheChanges.subscribeForCompetitionChanges(-1, false);
              this.eventsGroup = x;
              this.cacheChanges.removeAllEventsChangesSubscription(this.isLive);
              if (this.eventsGroup) {
                const eventIds = [];
                this.eventsGroup.events.map(e => {
                  eventIds.push(e.id);
                                   // this.cacheChanges.subscribeForAllEventChanges(e.id, this.isLive);
                });
                this.cacheChanges.addAllEventsChangesSubscription(eventIds, this.isLive);
              }
              this.loaderService.showHideLoader(false);
            }
          );
      } else {
        if (!this.sportRegionCompetitionSelection.region) {
          this.eventsGroup = null;
          return;
        }

        this.cache.getEventsGroupForSpecialOfferFilter(
          this.sportsService.timeFilterValueGet,
          this.languageCulture,
          this.dateToStringFormat,
          this.sportRegionCompetitionSelection.region.id,
          this.sportRegionCompetitionSelection.sport.specialOfferType).subscribe(
            x => {
              this.eventsGroup = x;
              this.cacheChanges.removeAllEventsChangesSubscription(this.isLive);
              if (this.eventsGroup) {
                // this.eventsGroup.events.map(e => {
                //   if (this.eventToExpandId && e.id === this.eventToExpandId) {
                //     this.expandQueryEvent(e);
                //   }
                //   this.cacheChanges.subscribeForAllEventChanges(e.id, this.isLive);
                // });
                this.cacheChanges.addAllEventsChangesSubscription(this.eventsGroup.events.map(e => e.id), this.isLive);

              }
              this.loaderService.showHideLoader(false);
            }
          );
      }
    }
  }

  private getEventsStartingSoon(clearData: boolean = false) {

    this.cache.getEventsGroupForEventsStartingSoonForDate(this.sportStartingSoonOrMostPopularSportId,
      this.currentPage,
      clearData,
      this.languageCulture,
      this.dateToStringFormat,
      this.sportsService.timeFilterValueGet,
      this.eventTypesForStandadOffer).subscribe(x => {
        this.eventsGroup = x;
        this.cacheChanges.removeAllEventsChangesSubscription(this.isLive);
        if (this.eventsGroup) {
          // this.eventsGroup.events.map(e => {
          //   if (this.eventToExpandId && e.id === this.eventToExpandId) {
          //     this.expandQueryEvent(e);
          //   }
          //   this.cacheChanges.subscribeForAllEventChanges(e.id, this.isLive);
          // });
          this.cacheChanges.addAllEventsChangesSubscription(this.eventsGroup.events.map(e => e.id), this.isLive);

        }
        this.loaderService.showHideLoader(false);
      });

  }

  private getMostPopularEvents(clearData: boolean = false) {
    this.cache.getEventsGroupForMostPopularEvents(this.sportStartingSoonOrMostPopularSportId, clearData, this.languageCulture, this.dateToStringFormat).subscribe(x => {
      this.eventsGroup = x;
      this.cacheChanges.removeAllEventsChangesSubscription(this.isLive);
      if (this.eventsGroup) {
        // this.eventsGroup.events.map(e => {
        //   if (this.eventToExpandId && e.id === this.eventToExpandId) {
        //     this.expandQueryEvent(e);
        //   }
        //   this.cacheChanges.subscribeForAllEventChanges(e.id, this.isLive);
        // });
        this.cacheChanges.addAllEventsChangesSubscription(this.eventsGroup.events.map(e => e.id), this.isLive);

      }
      this.loaderService.showHideLoader(false);
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
